<template>
    <div class="import-feedback-drawer__step">
        <div class="import-feedback-drawer__header">
            <div class="import-feedback-drawer__search-bar">
                <search
                    v-model="search"
                    :full-width="true"
                    :placeholder="$t('vocImportFeedbackDrawer.sourceChooser.search.placeholder')" />
            </div>
            <div
                v-if="headerSource"
                class="import-feedback-drawer__header-source"
                :data-cy="headerSource.dataCyTag || 'import-feedback-header-source'"
                @click="headerSource.onClick">
                <img
                    :src="headerSource.icon"
                    height="32px"
                    width="32px">
                <h4 class="import-feedback-drawer__header-source-title">
                    {{ headerSource.title }}
                </h4>
                <pendo-button
                    class="import-feedback-drawer__header-source-button"
                    :label="headerSource.buttonLabel"
                    theme="app"
                    type="secondary" />
            </div>
        </div>
        <div v-if="searchResults.length === 0">
            <pendo-empty-state
                class="import-feedback-drawer__empty-state"
                :title="$t('vocImportFeedbackDrawer.sourceChooser.emptyState.title')"
                :description="$t('vocImportFeedbackDrawer.sourceChooser.emptyState.description')" />
        </div>
        <div
            v-for="sourceGroup in sourceGroups"
            v-else
            :key="sourceGroup.id"
            class="import-feedback-drawer__source-group">
            <p class="import-feedback-drawer__group-title">
                {{ sourceGroup.name }}
            </p>
            <div class="import-feedback-drawer__source-cards">
                <import-feedback-source-card
                    v-for="source in sourceGroup.sources"
                    :key="source.id"
                    :source-config="source"
                    :data-cy="source.dataCyTag || 'import-feedback-source-card'"
                    @close="closeDrawer" />
            </div>
        </div>
    </div>
</template>

<script>
import Search from '@/common/search/search.vue';
import ImportFeedbackSourceCard from '@/voc/components/drawers/import-feedback-drawer/components/import-feedback-source-card/import-feedback-source-card.vue';
import { hasExploreEnabled, hasFreemiumEnabled, hasSetupExplore } from '@/voc/helpers/permissions.helper';
import { PendoButton, PendoEmptyState } from '@pendo/components';
import { PROVIDER_NAMES } from '@/integrations/integrations.constants';
import get from 'lodash/get';
import { mapGetters } from 'vuex';
const INTEGRATE_SF_LEARN_MORE = 'https://support.pendo.io/hc/en-us/articles/21264739646491';
const INTEGRATE_ZENDESK_LEARN_MORE = 'https://support.pendo.io/hc/en-us/articles/37644363245979';
const GONG_LEARN_MORE = 'https://support.pendo.io/hc/en-us/articles/39935055271067';
const TEAMS_LEARN_MORE = 'https://support.pendo.io/hc/en-us/articles/41432226725147';
const ZOOM_LEARN_MORE = 'https://support.pendo.io/hc/en-us/articles/41024270101403';
/* eslint-disable-next-line internal-rules/no-hardcoded-colors */
const PENDO_API_DOCS = 'https://engageapi.pendo.io/#e7ae3341-4498-4a4b-a871-4eecb6853d13';
const SOURCE_GROUP_IDS = {
    HEADER_SOURCE: 'headerSource',
    PENDO_SOURCES: 'pendoSources',
    CRM_SOURCES: 'crmSources',
    SUPPORT_TOOLS: 'supportTools',
    COLLABORATION_TOOLS: 'collaborationTools',
    CALL_TRANSCRIPTS: 'callTranscripts',
};

export default {
    components: {
        ImportFeedbackSourceCard,
        Search,
        PendoEmptyState,
        PendoButton,
    },
    emits: ['source-selected', 'close-drawer'],
    data () {
        return {
            search: '',
            isFreemium: hasFreemiumEnabled(),
        };
    },
    computed: {
        ...mapGetters({
            hasProductLinePurchased: 'productLines/hasProductLinePurchased',
            productLineByType: 'productLines/productLineByType',
            isAdmin: 'auth/isAdmin',
        }),
        hasPurchasedNPS () {
            return this.hasProductLinePurchased('nps');
        },
        hasPurchasedGuides () {
            return this.hasProductLinePurchased('guides');
        },
        hasPurchasedIdeas () {
            return this.hasProductLinePurchased('voc', 'ideas');
        },
        sourceGroups () {
            return [
                {
                    id: SOURCE_GROUP_IDS.PENDO_SOURCES,
                    name: this.$t('vocImportFeedbackDrawer.sourceChooser.groups.pendoSources'),
                    sources: this.searchResults.filter((source) => source.group === SOURCE_GROUP_IDS.PENDO_SOURCES),
                },
                {
                    id: SOURCE_GROUP_IDS.CRM_SOURCES,
                    name: this.$t('vocImportFeedbackDrawer.sourceChooser.groups.crm'),
                    sources: this.searchResults.filter((source) => source.group === SOURCE_GROUP_IDS.CRM_SOURCES),
                },
                {
                    id: SOURCE_GROUP_IDS.SUPPORT_TOOLS,
                    name: this.$t('vocImportFeedbackDrawer.sourceChooser.groups.supportTools'),
                    sources: this.searchResults.filter((source) => source.group === SOURCE_GROUP_IDS.SUPPORT_TOOLS),
                },
                {
                    id: SOURCE_GROUP_IDS.COLLABORATION_TOOLS,
                    name: this.$t('vocImportFeedbackDrawer.sourceChooser.groups.collaborationTools'),
                    sources: this.searchResults.filter(
                        (source) => source.group === SOURCE_GROUP_IDS.COLLABORATION_TOOLS,
                    ),
                },
                {
                    id: SOURCE_GROUP_IDS.CALL_TRANSCRIPTS,
                    name: this.$t('vocImportFeedbackDrawer.sourceChooser.groups.callTranscripts'),
                    sources: this.searchResults.filter((source) => source.group === SOURCE_GROUP_IDS.CALL_TRANSCRIPTS),
                },
            ].filter((group) => group.sources.length > 0);
        },
        sources () {
            return [
                {
                    title: this.$t('vocImportFeedbackDrawer.sourceCard.guide.title'),
                    id: 'guide',
                    description: this.$t('vocImportFeedbackDrawer.sourceCard.guide.description'),
                    icon: require('@img/voc/source-icon/guide-icon.svg'),
                    group: SOURCE_GROUP_IDS.PENDO_SOURCES,
                    buttonLabel: this.$t('vocImportFeedbackDrawer.sourceCard.guide.buttonLabel'),
                    onClick: () => this.selectSource('guide'),
                    isAvailable: true,
                    productLineCheck: this.hasPurchasedGuides,
                    upgradeAction: () => this.contactSales('guide'),
                    hasPermission: true,
                    dataCyTag: 'import-feedback-guide-source',
                },
                {
                    title: this.$t('vocImportFeedbackDrawer.sourceCard.nps.title'),
                    id: 'nps',
                    description: this.$t('vocImportFeedbackDrawer.sourceCard.nps.description'),
                    icon: require('@img/voc/source-icon/NPS-icon.svg'),
                    group: SOURCE_GROUP_IDS.PENDO_SOURCES,
                    buttonLabel: this.$t('vocImportFeedbackDrawer.sourceCard.nps.buttonLabel'),
                    onClick: () => this.selectSource('NPS'),
                    isAvailable: true,
                    productLineCheck: this.hasPurchasedNPS,
                    upgradeAction: () => this.contactSales('NPS'),
                    hasPermission: true,
                    dataCyTag: 'import-feedback-nps-source',
                },
                {
                    title: this.$t('vocImportFeedbackDrawer.sourceCard.ideaPortal.title'),
                    id: 'ideaPortal',
                    description: this.$t('vocImportFeedbackDrawer.sourceCard.ideaPortal.description'),
                    icon: require('@img/voc/source-icon/portal-icon.svg'),
                    group: SOURCE_GROUP_IDS.PENDO_SOURCES,
                    buttonLabel: this.$t('vocImportFeedbackDrawer.sourceCard.ideaPortal.buttonLabel'),
                    onClick: () => this.goTo({ isRoute: true, value: { name: 'VocPortals' } }),
                    isAvailable: true,
                    productLineCheck: this.hasPurchasedIdeas && !this.isFreemium,
                    hasPermission: true,
                },
                {
                    title: this.$t('vocImportFeedbackDrawer.sourceCard.salesforce.title'),
                    id: 'salesforce',
                    description: this.$t('vocImportFeedbackDrawer.sourceCard.salesforce.description'),
                    icon: require('@img/voc/source-icon/Salesforce-icon.svg'),
                    group: SOURCE_GROUP_IDS.CRM_SOURCES,
                    buttonLabel: this.$t('vocImportFeedbackDrawer.sourceCard.salesforce.buttonLabel'),
                    onClick: () =>
                        this.goTo({
                            isRoute: true,
                            value: {
                                name: 'IntegrationsProvider',
                                params: { integrationProvider: PROVIDER_NAMES.SALESFORCE_FOR_LISTEN },
                            },
                        }),
                    noPermissionAction: () => this.goTo({ isRoute: false, value: INTEGRATE_SF_LEARN_MORE }),
                    isAvailable: true,
                    hasPermission: this.isAdmin,
                    productLineCheck: this.hasMetadata('salesforce'),
                    upgradeAction: () => this.goTo({ isRoute: false, value: INTEGRATE_SF_LEARN_MORE }),
                },
                {
                    title: this.$t('vocImportFeedbackDrawer.sourceCard.csv.title'),
                    id: 'csv',
                    description: this.$t('vocImportFeedbackDrawer.sourceCard.csv.description'),
                    icon: require('@img/voc/source-icon/CSV-icon.svg'),
                    group: SOURCE_GROUP_IDS.HEADER_SOURCE,
                    buttonLabel: this.$t('vocImportFeedbackDrawer.sourceCard.csv.buttonLabel'),
                    onClick: () => this.selectSource('file'),
                    isAvailable: true,
                    hasPermission: true,
                    dataCyTag: 'import-feedback-file-source',
                },
                {
                    title: this.$t('vocImportFeedbackDrawer.sourceCard.zendesk.title'),
                    id: 'zendesk',
                    description: this.$t('vocImportFeedbackDrawer.sourceCard.zendesk.description'),
                    icon: require('@img/voc/source-icon/ZendeskTickets-icon.svg'),
                    group: SOURCE_GROUP_IDS.SUPPORT_TOOLS,
                    buttonLabel: this.$t('vocImportFeedbackDrawer.sourceCard.zendesk.buttonLabel'),
                    onClick: () =>
                        this.goTo({
                            isRoute: true,
                            value: {
                                name: 'IntegrationsProvider',
                                params: { integrationProvider: PROVIDER_NAMES.ZENDESK_FOR_LISTEN },
                            },
                        }),
                    isAvailable: true,
                    hasPermission: this.isAdmin,
                    noPermissionAction: () =>
                        this.goTo({ isRoute: false, value: INTEGRATE_ZENDESK_LEARN_MORE }),
                    productLineCheck: this.hasMetadata('zendesk') && hasExploreEnabled(),
                    requiresExploreSetup: !hasSetupExplore(),
                    upgradeAction: () =>
                        this.goTo({ isRoute: false, value: INTEGRATE_ZENDESK_LEARN_MORE }),
                },
                {
                    title: this.$t('vocImportFeedbackDrawer.sourceCard.intercom.title'),
                    id: 'intercom',
                    description: this.$t('vocImportFeedbackDrawer.sourceCard.intercom.description'),
                    icon: require('@img/voc/source-icon/Intercom-icon.svg'),
                    group: SOURCE_GROUP_IDS.SUPPORT_TOOLS,
                    buttonLabel: this.$t('vocImportFeedbackDrawer.sourceCard.intercom.buttonLabel'),
                    isAvailable: false,
                    hasPermission: true,
                },
                {
                    title: this.$t('vocImportFeedbackDrawer.sourceCard.hubspot.title'),
                    id: 'hubspot',
                    description: this.$t('vocImportFeedbackDrawer.sourceCard.hubspot.description'),
                    icon: require('@img/voc/source-icon/Hubspot-icon.svg'),
                    group: SOURCE_GROUP_IDS.CRM_SOURCES,
                    buttonLabel: this.$t('vocImportFeedbackDrawer.sourceCard.hubspot.buttonLabel'),
                    isAvailable: false,
                    hasPermission: true,
                },
                {
                    title: this.$t('vocImportFeedbackDrawer.sourceCard.slack.title'),
                    id: 'slack',
                    description: this.$t('vocImportFeedbackDrawer.sourceCard.slack.description'),
                    icon: require('@img/voc/source-icon/SlackMessages-icon.svg'),
                    group: SOURCE_GROUP_IDS.COLLABORATION_TOOLS,
                    buttonLabel: this.$t('vocImportFeedbackDrawer.sourceCard.slack.buttonLabel'),
                    isAvailable: false,
                    hasPermission: true,
                },
                {
                    title: this.$t('vocImportFeedbackDrawer.sourceCard.teamsChat.title'),
                    id: 'teamsChat',
                    description: this.$t('vocImportFeedbackDrawer.sourceCard.teamsChat.description'),
                    icon: require('@img/voc/source-icon/Teams-icon.svg'),
                    group: SOURCE_GROUP_IDS.COLLABORATION_TOOLS,
                    buttonLabel: this.$t('vocImportFeedbackDrawer.sourceCard.teamsChat.buttonLabel'),
                    isAvailable: false,
                    hasPermission: true,
                },
                {
                    title: this.$t('vocImportFeedbackDrawer.sourceCard.gong.title'),
                    id: 'gong',
                    description: this.$t('vocImportFeedbackDrawer.sourceCard.gong.description'),
                    icon: require('@img/voc/source-icon/Gong-icon.svg'),
                    group: SOURCE_GROUP_IDS.CALL_TRANSCRIPTS,
                    buttonLabel: this.$t('vocImportFeedbackDrawer.sourceCard.gong.buttonLabel'),
                    isAvailable: true,
                    hasPermission: this.isAdmin,
                    noPermissionAction: () =>
                        this.goTo({ isRoute: false, value: GONG_LEARN_MORE }),
                    productLineCheck: this.hasMetadata('gong') && hasExploreEnabled(),
                    requiresExploreSetup: !hasSetupExplore(),
                    upgradeAction: () =>
                        this.goTo({ isRoute: false, value: GONG_LEARN_MORE }),
                    onClick: () =>
                        this.goTo({
                            isRoute: true,
                            value: {
                                name: 'IntegrationsProvider',
                                params: { integrationProvider: PROVIDER_NAMES.GONG_FOR_LISTEN },
                            },
                        }),
                },
                {
                    title: this.$t('vocImportFeedbackDrawer.sourceCard.zoom.title'),
                    id: 'zoom',
                    description: this.$t('vocImportFeedbackDrawer.sourceCard.zoom.description'),
                    icon: require('@img/voc/source-icon/Zoom-icon.svg'),
                    group: SOURCE_GROUP_IDS.CALL_TRANSCRIPTS,
                    buttonLabel: this.$t('vocImportFeedbackDrawer.sourceCard.zoom.buttonLabel'),
                    isAvailable: true,
                    hasPermission: this.isAdmin,
                    noPermissionAction: () =>
                        this.goTo({ isRoute: false, value: ZOOM_LEARN_MORE }),
                    productLineCheck: this.hasMetadata('zoom') && hasExploreEnabled(),
                    requiresExploreSetup: !hasSetupExplore(),
                    upgradeAction: () =>
                        this.goTo({ isRoute: false, value: ZOOM_LEARN_MORE }),
                    onClick: () =>
                        this.goTo({
                            isRoute: true,
                            value: {
                                name: 'IntegrationsProvider',
                                params: { integrationProvider: PROVIDER_NAMES.ZOOM_FOR_LISTEN },
                            },
                        }),
                },
                {
                    title: this.$t('vocImportFeedbackDrawer.sourceCard.teamsTranscripts.title'),
                    id: 'teamsTranscripts',
                    description: this.$t('vocImportFeedbackDrawer.sourceCard.teamsTranscripts.description'),
                    icon: require('@img/voc/source-icon/Teams-icon.svg'),
                    group: SOURCE_GROUP_IDS.CALL_TRANSCRIPTS,
                    buttonLabel: this.$t('vocImportFeedbackDrawer.sourceCard.teamsTranscripts.buttonLabel'),
                    isAvailable: true,
                    isBeta: true,
                    hasPermission: this.isAdmin,
                    noPermissionAction: () =>
                        this.goTo({ isRoute: false, value: TEAMS_LEARN_MORE }),
                    productLineCheck: this.hasMetadata('teams') && hasExploreEnabled(),
                    requiresExploreSetup: !hasSetupExplore(),
                    upgradeAction: () =>
                        this.goTo({ isRoute: false, value: TEAMS_LEARN_MORE }),
                    onClick: () =>
                        this.goTo({
                            isRoute: true,
                            value: {
                                name: 'IntegrationsProvider',
                                params: { integrationProvider: PROVIDER_NAMES.TEAMS_FOR_LISTEN },
                            },
                        }),
                },
                {
                    title: this.$t('vocImportFeedbackDrawer.sourceCard.api.title'),
                    id: 'api',
                    description: this.$t('vocImportFeedbackDrawer.sourceCard.api.description'),
                    icon: require('@img/voc/source-icon/API-icon.svg'),
                    group: SOURCE_GROUP_IDS.PENDO_SOURCES,
                    buttonLabel: this.$t('vocImportFeedbackDrawer.sourceCard.api.buttonLabel'),
                    isAvailable: true,
                    hasPermission: true,
                    onClick: () => this.goTo({ isRoute: false, value: PENDO_API_DOCS }),
                },
            ];
        },
        searchResults () {
            if (this.search) {
                return this.sources.filter((source) => source.title.toLowerCase().includes(this.search.toLowerCase()));
            }

            return this.sources;
        },
        headerSource () {
            return this.sources.find((source) => source.group === SOURCE_GROUP_IDS.HEADER_SOURCE);
        },
    },
    methods: {
        hasMetadata (integration) {
            if (!this.hasProductLinePurchased('voc')) return false;

            const vocPl = this.productLineByType('voc');

            return get(vocPl.metadata, `integrations.${integration}.enabled`, false);
        },
        selectSource (source) {
            this.$emit('source-selected', source);
        },
        goTo ({ isRoute, value }) {
            if (isRoute) {
                this.closeDrawer();
                this.$router.push(value);
            } else {
                window.open(value, '_blank');
            }
        },
        contactSales (source) {
            const contactSalesUrl =
                source === 'NPS'
                    ? 'https://www.pendo.io/product/features/nps/?q_showExperience=experience-1733840315209'
                    : 'https://www.pendo.io/product/in-app-guides/?q_showExperience=experience-1733840479417';
            window.open(contactSalesUrl, '_blank');
        },
        closeDrawer () {
            this.$emit('close-drawer');
        },
    },
};
</script>

<style lang="less" scoped>
.import-feedback-drawer {
    &__step {
        display: flex;
        justify-content: flex-start;
        flex-direction: column;
        width: 90%;
        margin: 0 auto;
        gap: 24px;
        align-items: center;
    }

    .pendo-card {
        width: fit-content;

        :deep(.pendo-card__body) {
            padding: 32px 32px 0 32px;
            display: flex;
            flex-direction: column;
            align-items: center;
        }
    }

    &__source-other-option-button {
        margin: 20px 0;
    }

    &__source-cards {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr 1fr;
        grid-auto-rows: auto;
        gap: 16px;
        @media (max-width: 900px) {
            grid-template-columns: 1fr 1fr 1fr;
        }
        @media (max-width: 650px) {
            grid-template-columns: 1fr 1fr;
        }
        @media (max-width: 450px) {
            grid-template-columns: 1fr;
        }
    }
    &__group-title {
        font-size: 16px;
        font-weight: 600;
    }
    &__search-bar {
        width: 100%;
        background: var(--palette-gray-0);
        padding: 16px;
        border: 1px solid var(--palette-gray-40);
        border-radius: 3px;
    }
    &__empty-state {
        position: relative;
        background: transparent !important;
        :deep(.pendo-empty-state__inner) {
            width: 100%;
        }
    }
    &__header {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 10px;
        width: 100%;
        margin-bottom: 16px;
    }
    &__header-source {
        display: flex;
        align-items: center;
        gap: 16px;
        background: var(--palette-gray-0);
        height: 70px;
        padding: 0 16px;
        border: 1px solid var(--palette-gray-40);
        border-radius: 3px;
        width: 100%;
        cursor: pointer;
    }
    &__header-source-title {
        font-size: 16px;
        font-weight: 600;
        margin: 0;
    }
    &__header-source-button {
        margin-left: auto;
    }
    &__source-group {
        width: 100%;
    }
}
</style>
